import React, { useEffect, useRef, useState } from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { Search, Menu, X, ChevronDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_napoli-artigianalita/artifacts/ff6wp22h_logo%20CC.png";

const STORIA_ITEMS = [
  { i18nKey: "nav.origini", to: "/le-origini" },
  { i18nKey: "nav.tempo", to: "/nel-tempo" },
  { i18nKey: "nav.road", to: "/on-the-road" },
];

const NAV = [
  { i18nKey: "nav.storia", children: STORIA_ITEMS },
  { i18nKey: "nav.tostatura", to: "/la-tostatura" },
  { i18nKey: "nav.miscele", to: "/le-miscele" },
  { i18nKey: "nav.camillo", to: "/zio-camillo" },
  { i18nKey: "nav.mondo", to: "/nel-mondo" },
  { i18nKey: "nav.contatti", to: "/#contatti" },
];

const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [storiaOpen, setStoriaOpen] = useState(false);
  const [mobileStoriaOpen, setMobileStoriaOpen] = useState(false);

  const storiaWrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (storiaWrapRef.current && !storiaWrapRef.current.contains(e.target)) {
        setStoriaOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setStoriaOpen(true);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setStoriaOpen(false), 150);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled ? "backdrop-blur-md shadow-md" : ""
      }`}
      style={{
        background: scrolled ? "rgba(251,245,233,0.94)" : "#fbf5e9",
        borderBottom: "1px solid rgba(91,54,33,0.12)",
      }}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 focus-ring" data-testid="header-logo">
            <img
              src={LOGO_URL}
              alt="Caffè Cola 1960"
              className="h-14 lg:h-16 w-auto object-contain"
              style={{ mixBlendMode: "multiply" }}
              loading="eager"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Primary">
            {NAV.map((item) =>
              item.children ? (
                <div
                  key={item.i18nKey}
                  className="relative"
                  ref={storiaWrapRef}
                  onMouseEnter={handleEnter}
                  onMouseLeave={handleLeave}
                >
                  <button
                    onClick={() => setStoriaOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={storiaOpen}
                    className="inline-flex items-center gap-1 text-[12px] xl:text-[13px] font-semibold uppercase tracking-wider hover:text-[#c5371f] transition-colors focus-ring"
                    style={{ color: storiaOpen ? "#c5371f" : "#3b2417" }}
                    data-testid="nav-storia-btn"
                  >
                    {t(item.i18nKey)}
                    <ChevronDown
                      size={13}
                      className={`transition-transform duration-200 ${
                        storiaOpen ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>

                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50 transition-all duration-200 ${
                      storiaOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                    role="menu"
                  >
                    <div
                      className="min-w-[260px] rounded-xl shadow-xl overflow-hidden"
                      style={{
                        background: "#fbf5e9",
                        border: "1px solid rgba(91,54,33,0.15)",
                        boxShadow: "0 20px 40px -15px rgba(42,22,12,0.25)",
                      }}
                    >
                      <div className="h-1 w-full" style={{ background: "#c5371f" }} />
                      <ul className="py-2">
                        {item.children.map((sub) => (
                          <li key={sub.i18nKey}>
                            <Link
                              to={sub.to}
                              onClick={() => setStoriaOpen(false)}
                              className="flex items-center justify-between px-5 py-3 text-sm font-medium uppercase tracking-wider transition-colors hover:bg-[#ecdcbd] hover:text-[#c5371f]"
                              style={{ color: "#3b2417" }}
                            >
                              <span>{t(sub.i18nKey)}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.i18nKey}
                  to={item.to}
                  className="text-[12px] xl:text-[13px] font-semibold uppercase tracking-wider hover:text-[#c5371f] transition-colors focus-ring"
                  style={{ color: "#3b2417" }}
                >
                  {t(item.i18nKey)}
                </Link>
              )
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-full hover:bg-[#ecdcbd] transition focus-ring"
              aria-label={t("nav.search")}
              data-testid="header-search-btn"
            >
              <Search size={20} style={{ color: "#3b2417" }} />
            </button>
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden p-2 rounded-full hover:bg-[#ecdcbd] transition focus-ring"
              aria-label={t("nav.openMenu")}
              data-testid="header-menu-btn"
            >
              <Menu size={22} style={{ color: "#3b2417" }} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="pb-4" data-testid="header-search-row">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7a4a2e" }} />
              <input
                type="text"
                placeholder={t("nav.search")}
                className="cc-input pl-10"
                autoFocus
                data-testid="header-search-input"
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" aria-modal="true" role="dialog" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside
            className="absolute right-0 top-0 h-full w-80 max-w-[85%] shadow-2xl p-6 overflow-y-auto"
            style={{ background: "#fbf5e9" }}
          >
            <div className="flex items-center justify-between mb-6">
              <img src={LOGO_URL} alt="" className="h-12 w-auto" style={{ mixBlendMode: "multiply" }} />
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-full hover:bg-[#ecdcbd]"
                aria-label={t("nav.closeMenu")}
                data-testid="mobile-drawer-close"
              >
                <X size={22} style={{ color: "#3b2417" }} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((item) =>
                item.children ? (
                  <div key={item.i18nKey} className="border-b border-[#e0c89a]/60">
                    <button
                      onClick={() => setMobileStoriaOpen((v) => !v)}
                      className="w-full flex items-center justify-between py-3 px-2 text-base font-medium uppercase tracking-wider hover:text-[#c5371f]"
                      style={{ color: "#3b2417" }}
                      aria-expanded={mobileStoriaOpen}
                    >
                      <span>{t(item.i18nKey)}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${mobileStoriaOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        mobileStoriaOpen ? "max-h-80" : "max-h-0"
                      }`}
                    >
                      <ul className="pl-3 pb-2">
                        {item.children.map((sub) => (
                          <li key={sub.i18nKey}>
                            <Link
                              to={sub.to}
                              onClick={() => {
                                setDrawerOpen(false);
                                setMobileStoriaOpen(false);
                              }}
                              className="block py-2.5 px-2 text-sm font-medium uppercase tracking-wider hover:text-[#c5371f]"
                              style={{ color: "#5b3621" }}
                            >
                              {t(sub.i18nKey)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.i18nKey}
                    to={item.to}
                    onClick={() => setDrawerOpen(false)}
                    className="py-3 px-2 text-base font-medium uppercase tracking-wider border-b border-[#e0c89a]/60 hover:text-[#c5371f]"
                    style={{ color: "#3b2417" }}
                  >
                    {t(item.i18nKey)}
                  </Link>
                )
              )}
            </nav>
          </aside>
        </div>
      )}
    </header>
  );
};

export default Header;
